import { useCallback, useEffect, useRef, useState } from 'react';
import { Database, onChildAdded, ref, remove } from 'firebase/database';

export interface PendingCommunicationRealtimeEvent {
  eventId: string;
  eventType?: string;
  transactionUid?: string;
  transactionKey?: string;
  teamUid?: string;
  sourceUid?: string;
  unreadChatCount?: number;
  unreadCount?: number;
  unreadMessages?: number;
  unreadMessagesCount?: number;
  count?: number;
  occurredAt?: string | number;
  expiresAt?: string | number;
  schemaVersion?: string | number;
}

export interface RealtimeNotificationState {
  open: boolean;
  pendingCount: number;
  eventIds: string[];
  message: string;
  lastEventAt: number | null;
}

interface UsePendingCommunicationRealtimeOptions {
  database: Database | null;
  recipientUid?: string | null;
  debounceMs?: number;
  flushStrategy?: 'debounce' | 'immediate';
  staleWindowMs?: number;
  removeHandledFromDb?: boolean;
  showSnackbar?: boolean;
}

const DEFAULT_DEBOUNCE_MS = 5000;
const DEFAULT_STALE_WINDOW_MS = 10 * 60 * 1000;
const DEFAULT_MESSAGE = 'New Unread Chat/Missed Call. Please refresh to view latest missed calls/unread chats.';

const initialNotificationState: RealtimeNotificationState = {
  open: false,
  pendingCount: 0,
  eventIds: [],
  message: DEFAULT_MESSAGE,
  lastEventAt: null
};

const parseTimestamp = (value?: string | number): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const isStaleEvent = (event: PendingCommunicationRealtimeEvent, staleWindowMs: number): boolean => {
  const now = Date.now();
  const occurredAtTs = parseTimestamp(event.occurredAt);
  const expiresAtTs = parseTimestamp(event.expiresAt);

  if (occurredAtTs !== null && now - occurredAtTs > staleWindowMs) {
    return true;
  }

  if (expiresAtTs !== null && expiresAtTs <= now) {
    return true;
  }

  return false;
};

const getTransactionLabel = (event: PendingCommunicationRealtimeEvent): string => {
  const label = event.transactionKey || event.transactionUid;
  return label && String(label).trim().length > 0 ? String(label) : 'Unknown';
};

const getUnreadMessageCount = (event: PendingCommunicationRealtimeEvent): number | null => {
  const candidates = [
    event.unreadChatCount,
    event.unreadCount,
    event.unreadMessagesCount,
    event.unreadMessages,
    event.count
  ];

  for (const value of candidates) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }
  }

  return null;
};

const getEventSpecificMessage = (event: PendingCommunicationRealtimeEvent): string => {
  const transactionLabel = getTransactionLabel(event);
  const eventType = (event.eventType || '').toLowerCase();

  if (eventType.includes('chat')) {
    const unreadCount = getUnreadMessageCount(event) ?? 1;
    const unreadText = `${unreadCount} unread message${unreadCount === 1 ? '' : 's'}`;
    return `Unread chat: ${unreadText} on transaction ${transactionLabel}`;
  }

  if (eventType.includes('call') || eventType.includes('missed')) {
    return `You have a missed call on transaction ${transactionLabel}`;
  }

  return `Pending communication in transaction: ${transactionLabel}`;
};

const usePendingCommunicationRealtime = ({
  database,
  recipientUid,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  flushStrategy = 'debounce',
  staleWindowMs = DEFAULT_STALE_WINDOW_MS,
  removeHandledFromDb = true,
  showSnackbar = true
}: UsePendingCommunicationRealtimeOptions) => {
  const [notification, setNotification] = useState<RealtimeNotificationState>(initialNotificationState);

  const seenEventIdsRef = useRef<Set<string>>(new Set());
  const handledEventIdsRef = useRef<Set<string>>(new Set());
  const bufferedEventsRef = useRef<PendingCommunicationRealtimeEvent[]>([]);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const immediateFlushScheduledRef = useRef<boolean>(false);

  const flushBufferedEvents = useCallback(() => {
    const buffered = bufferedEventsRef.current;
    if (!buffered.length) return;

    const incomingEventIds = buffered.map((event) => event.eventId);
    const latestEvent = buffered[buffered.length - 1];
    const latestMessage = latestEvent ? getEventSpecificMessage(latestEvent) : DEFAULT_MESSAGE;
    bufferedEventsRef.current = [];

    setNotification((prev) => {
      const mergedEventIds = Array.from(new Set([...prev.eventIds, ...incomingEventIds]));
      const mergedCount = prev.pendingCount + incomingEventIds.length;

      const message =
        mergedCount > 1 ? `Unreadchat/missed calls (${mergedCount}). ${latestMessage}` : latestMessage;

      return {
        ...prev,
        open: showSnackbar ? true : false,
        pendingCount: mergedCount,
        eventIds: mergedEventIds,
        message,
        lastEventAt: Date.now()
      };
    });
  }, [showSnackbar]);

  const clearDebounceTimer = useCallback(() => {
    if (!debounceTimerRef.current) return;
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = null;
  }, []);

  const scheduleImmediateFlush = useCallback(() => {
    if (immediateFlushScheduledRef.current) return;
    immediateFlushScheduledRef.current = true;

    const run = () => {
      immediateFlushScheduledRef.current = false;
      flushBufferedEvents();
    };

    if (typeof queueMicrotask === 'function') {
      queueMicrotask(run);
    } else {
      Promise.resolve().then(run);
    }
  }, [flushBufferedEvents]);

  const clearAndHandleEvents = useCallback(async () => {
    clearDebounceTimer();

    const bufferedEventIds = bufferedEventsRef.current.map((event) => event.eventId);
    bufferedEventsRef.current = [];

    const allHandledIds = Array.from(new Set([...notification.eventIds, ...bufferedEventIds]));

    allHandledIds.forEach((eventId) => handledEventIdsRef.current.add(eventId));

    if (removeHandledFromDb && database && recipientUid && allHandledIds.length > 0) {
      await Promise.all(
        allHandledIds.map(async (eventId) => {
          try {
            await remove(ref(database, `pending-communications/${recipientUid}/${eventId}`));
          } catch {
            // Ignore permission or transient removal failures; local handled set prevents repeats.
          }
        })
      );
    }

    setNotification(initialNotificationState);
  }, [clearDebounceTimer, database, notification.eventIds, recipientUid, removeHandledFromDb]);

  const acknowledgeAndClear = useCallback(async () => {
    await clearAndHandleEvents();
  }, [clearAndHandleEvents]);

  const dismissAndClear = useCallback(async () => {
    await clearAndHandleEvents();
  }, [clearAndHandleEvents]);

  useEffect(() => {
    clearDebounceTimer();
    bufferedEventsRef.current = [];
    seenEventIdsRef.current.clear();
    handledEventIdsRef.current.clear();
    immediateFlushScheduledRef.current = false;
    setNotification(initialNotificationState);

    if (!database || !recipientUid) {
      return;
    }

    const notificationsRef = ref(database, `pending-communications/${recipientUid}`);

    const unsubscribe = onChildAdded(notificationsRef, (snapshot) => {
      const rawValue = snapshot.val() as Partial<PendingCommunicationRealtimeEvent> | null;
      const eventId = rawValue?.eventId || snapshot.key;

      if (!eventId) return;

      if (seenEventIdsRef.current.has(eventId) || handledEventIdsRef.current.has(eventId)) {
        return;
      }

      const event: PendingCommunicationRealtimeEvent = {
        ...(rawValue || {}),
        eventId
      };

      if (isStaleEvent(event, staleWindowMs)) {
        seenEventIdsRef.current.add(eventId);
        return;
      }

      seenEventIdsRef.current.add(eventId);
      bufferedEventsRef.current.push(event);

      if (flushStrategy === 'immediate') {
        scheduleImmediateFlush();
        return;
      }

      if (!debounceTimerRef.current) {
        debounceTimerRef.current = setTimeout(() => {
          debounceTimerRef.current = null;
          flushBufferedEvents();
        }, debounceMs);
      }
    });

    return () => {
      unsubscribe();
      clearDebounceTimer();
      bufferedEventsRef.current = [];
      immediateFlushScheduledRef.current = false;
    };
  }, [clearDebounceTimer, database, debounceMs, flushBufferedEvents, flushStrategy, recipientUid, scheduleImmediateFlush, staleWindowMs]);

  return {
    notification,
    acknowledgeAndClear,
    dismissAndClear
  };
};

export default usePendingCommunicationRealtime;
